import Button from '@gaia/components/Button';
import type {UserLayerDispatcher} from '@gaia/hooks/useLayers';
import usePersistentState from '@gaia/hooks/usePersistentState';
import {useUser} from '@gaia/stores/user';
import theme from '@gaia/theme';
import {ampli} from '@gaia/utils/gaia/trackAnalytics';
import {Paper, Typography, useMediaQuery} from '@mui/material';
import {useState} from 'react';

import ActivitySelection from './ActivitySelection';
import AddMaps from './AddMaps';
import ControlsExplained from './ControlsExplained';
import LayersExplained from './LayersExplained';
import classes from './OnboardingPopup.module.css';
import SignUpCTA from './SignUpCTA';
import WelcomePage from './WelcomePage';

export interface OnboardingPopupProps extends React.HTMLAttributes<HTMLDivElement> {
    userLayerDispatcher: UserLayerDispatcher;
    layerTypes: Record<string, LayerType> | undefined;
    userLayers: UserLayer[] | undefined;
    onSidebarChange: SetState<SidebarState>;
}

const viewedAnonymousOnboardingKey = 'viewed_anonymous_onboarding';

export function OnboardingPopup({
    userLayerDispatcher,
    layerTypes,
    userLayers,
    onSidebarChange,
}: OnboardingPopupProps) {
    const {isAuthenticated} = useUser();

    const isMobile = useMediaQuery('(max-width:750px)');

    const [viewedAnonymousOnboarding, setViewedAnonymousOnboarding] = usePersistentState<boolean>(
        viewedAnonymousOnboardingKey,
        false
    );

    const [stepCount, setStepCount] = useState(0);
    const [selectedActivities, setSelectedActivities] = useState<string[]>([]);
    // check if user is viewing the map on a datasummary page
    const isViewingMainMap = window && window.location.href.includes('/map/');

    function onExitOnboarding() {
        setViewedAnonymousOnboarding(true);
    }

    if (isAuthenticated || viewedAnonymousOnboarding || isMobile || !isViewingMainMap) return null;

    return (
        <div className={classes.popup}>
            <Paper
                elevation={3}
                style={{
                    padding: theme.spacing(3),
                    position: 'relative',
                    pointerEvents: 'all',
                    textAlign: 'center',
                    width: stepCount !== 5 ? 350 : undefined,
                }}
            >
                <Typography
                    variant='body1'
                    style={{
                        position: 'absolute',
                        top: theme.spacing(2),
                        left: theme.spacing(3),
                        fontWeight: 500,
                    }}
                >
                    {stepCount + 1} of 6
                </Typography>

                <Button
                    appearance='secondary'
                    style={{
                        position: 'absolute',
                        top: theme.spacing(1),
                        right: theme.spacing(1),
                    }}
                    onClick={() => {
                        ampli.skipWebOnboarding({skipStep: stepCount});
                        onExitOnboarding();
                    }}
                >
                    Skip
                </Button>

                {stepCount === 0 && <WelcomePage setStepCount={setStepCount} />}
                {stepCount === 1 && (
                    <ActivitySelection
                        selectedActivities={selectedActivities}
                        setSelectedActivities={setSelectedActivities}
                        setStepCount={setStepCount}
                    />
                )}
                {stepCount === 2 && (
                    <AddMaps
                        selectedActivities={selectedActivities}
                        userLayerDispatcher={userLayerDispatcher}
                        layerTypes={layerTypes}
                        userLayers={userLayers}
                        setStepCount={setStepCount}
                        onSidebarChange={onSidebarChange}
                    />
                )}
                {stepCount === 3 && (
                    <LayersExplained
                        setStepCount={setStepCount}
                        onSidebarChange={onSidebarChange}
                    />
                )}
                {stepCount === 4 && <ControlsExplained setStepCount={setStepCount} />}
                {stepCount === 5 && (
                    <SignUpCTA
                        setStepCount={setStepCount}
                        selectedActivities={selectedActivities}
                        onExitOnboarding={onExitOnboarding}
                    />
                )}
            </Paper>
        </div>
    );
}
